import React, { memo } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Row, Table } from 'components';
import { DownloadIcon } from 'components/common/icons/DownloadIcon';
import { AttachmentsFilterDrawer } from './filter';
import { useAttachments } from './useAttachments';
import styles from './styles.module.scss';
import { AddEditModal } from './addEdit';
import { DeleteModal } from './delete';
import { MicroserviceTypes } from 'typings';
import { DeleteIcon, EditIcon, FilterIcon, PlusIcon } from 'components/common/icons';
import { convertEnumsToReadableStrings } from 'utils/helpers';

export interface AttachmentDataType {
  id: string;
  fileName: string;
  attachmentType: string;
  createDate: any;
  lastModifiedDate: any;
}

interface IAttachments {
  entityId: string;
  entityName:
    | 'BRAND'
    | 'CLIENT'
    | 'ADMIN'
    | 'TASK'
    | 'SUPPLIER'
    | 'LEAD'
    | 'DEAL'
    | 'ITEM'
    | 'PURCHASE_REQUEST'
    | 'PURCHASE_ORDER'
    | 'ORDER';
  service?: MicroserviceTypes;
  hasAccessToWrite: boolean;
  hasAccessToDelete: boolean;
}

export const Attachments = memo((props: IAttachments) => {
  const { entityId, entityName, service, hasAccessToWrite, hasAccessToDelete } = props;
  const {
    attachmentList,
    total,
    handleTableChange,
    isLoading,
    dataToEdit,
    pagination,
    onHandleDelete,
    onDeleteModalClose,
    deleteItem,
    onAddModalStateChange,
    openAddModal,
    filtersForm,
    applyFilters,
    resetFilters,
    onOpenFilters,
    onCloseFilters,
    filterDrawerState,
  } = useAttachments({ entityName, entityId, service });
  const columns: ColumnsType<AttachmentDataType> = [
    {
      title: 'File Name',
      key: 'fileName',
      sorter: true,
      width: 200,
      ellipsis: true,
      render: ({ files, fileName }) => {
        return files?.ATTACHMENTS ? (
          <Button
            type="link"
            icon={<DownloadIcon />}
            download
            target="_blank"
            className={styles.btn_download}
            title={fileName}
            rel="noopener noreferrer"
            href={files?.ATTACHMENTS}
          >
            <span className={styles.btn_download_text}>{fileName || 'Not Set'}</span>
          </Button>
        ) : (
          ''
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'attachmentType',
      key: 'attachmentType',
      render: (value) => convertEnumsToReadableStrings(value),
      sorter: true,
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreateDate',
      key: 'createDate',
      sorter: true,
      render: (value) => value?.day,
    },
    {
      title: 'Updated Date',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      render: (value) => value?.day,
      sorter: true,
    },
  ];
  if (hasAccessToDelete || hasAccessToWrite) {
    columns.push({
      fixed: 'right',
      width: 68,
      key: 'actions',
      render: (value, record) => (
        <Row className={'gap-8'} wrap={false} align={'middle'} justify={'center'}>
          {hasAccessToWrite && (
            <Button
              onClick={() => onAddModalStateChange(value)}
              style={{ padding: 0, width: 16, height: 16 }}
              disabled={['CLIENT_EMAIL', 'EMAIL', 'PURCHASE_ORDER'].includes(record.attachmentType)}
              type={'text'}
              size={'small'}
              icon={<EditIcon />}
            />
          )}
          {hasAccessToDelete && (
            <Button
              onClick={() => onHandleDelete(value)}
              style={{ padding: 0, width: 16, height: 16 }}
              type={'text'}
              size={'small'}
              icon={<DeleteIcon />}
            />
          )}
        </Row>
      ),
    });
  }
  return (
    <div className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'end'}>
            {hasAccessToWrite && (
              <Button type={'primary'} onClick={() => onAddModalStateChange()} icon={<PlusIcon />}>
                Add New
              </Button>
            )}
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'}>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <Table
                bordered
                size={'small'}
                columns={columns}
                dataSource={attachmentList || []}
                rowKey="id"
                loading={{
                  indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                  spinning: isLoading,
                }}
                locale={
                  isLoading
                    ? { emptyText: null } // Hide "No Data" text while loading
                    : {} // Use default AntD "No Data" icon when empty
                }
                pagination={{
                  position: ['bottomCenter'],
                  showSizeChanger: true,
                  showTotal: (total) => `Total ${total} items`,
                  current: pagination.page,
                  pageSize: pagination.size,
                  total: total,
                  size: 'default',
                }}
                onChange={handleTableChange as any}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <DeleteModal
        openModal={!!deleteItem}
        onDeleteModalClose={onDeleteModalClose}
        dataToDelete={deleteItem}
        service={service}
        entityName={entityName}
      />
      <AddEditModal
        modalState={openAddModal}
        onModalStateChange={onAddModalStateChange}
        entityName={entityName}
        service={service}
        entityId={entityId}
        dataToEdit={dataToEdit}
      />
      <AttachmentsFilterDrawer
        form={filtersForm}
        service={service}
        entityName={entityName}
        drawerOpen={filterDrawerState}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
    </div>
  );
});
